import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { ArrowLeft, Loader2, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatCurrencyEUR, formatDate } from "@/lib/utils";
import { useMdfAllocations } from "./use-mdf-allocations";
import { useCreateMdfDraft } from "./use-create-mdf-draft";
import type { MdfAllocationSummary } from "./types";

// Port of /app/portal/mdf/new/page.tsx. The legacy form lived on this page;
// the SPA's runtime view requires a draftId before it can render auto-save,
// so /new is a small allocation chooser that creates the draft and bounces
// to /portal/mdf/:id. The five-field form lives on the detail page when
// status='draft'.

export function MdfNewPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useMdfAllocations();
  const create = useCreateMdfDraft();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const allocations = data ?? [];

  function handleContinue() {
    if (!selectedId) return;
    create.mutate(
      { allocation_id: selectedId },
      {
        onSuccess: ({ id }) => {
          navigate(`/portal/mdf/${id}`, { replace: true });
        },
      },
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <Link
        to="/portal/mdf"
        className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" />
        Back to marketing funds
      </Link>

      <header className="mt-4">
        <h1 className="font-display text-2xl font-semibold tracking-tight">
          Start a new request
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Pick the allocation you want to spend against. You can edit details
          and add supporting documents on the next step.
        </p>
      </header>

      {isLoading ? (
        <div className="mt-6 space-y-3">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
        </div>
      ) : isError ? (
        <section className="mt-6 rounded-xl border bg-card p-8 text-center">
          <h2 className="font-display text-base font-semibold tracking-tight">
            Couldn&rsquo;t load allocations
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Please refresh the page. If the problem persists, contact support.
          </p>
        </section>
      ) : allocations.length === 0 ? (
        <section className="mt-6 rounded-xl border bg-card p-8 text-center">
          <Wallet className="mx-auto h-8 w-8 text-muted-foreground" />
          <h2 className="mt-3 font-display text-base font-semibold tracking-tight">
            No active fund
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            You don&rsquo;t have an active marketing fund to draw against right
            now. When one is opened for you, this page will let you start a
            request.
          </p>
          <Link
            to="/portal/mdf"
            className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
          >
            <ArrowLeft className="h-3 w-3" />
            Back to marketing funds
          </Link>
        </section>
      ) : (
        <>
          <ul role="radiogroup" className="mt-6 space-y-3">
            {allocations.map((a) => (
              <li key={a.allocation_id}>
                <AllocationOption
                  allocation={a}
                  selected={a.allocation_id === selectedId}
                  onSelect={() => setSelectedId(a.allocation_id)}
                  disabled={create.isPending}
                />
              </li>
            ))}
          </ul>

          <div className="mt-6 flex justify-end">
            <Button
              type="button"
              onClick={handleContinue}
              disabled={!selectedId || create.isPending}
            >
              {create.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Continue
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function AllocationOption({
  allocation: a,
  selected,
  onSelect,
  disabled,
}: {
  allocation: MdfAllocationSummary;
  selected: boolean;
  onSelect: () => void;
  disabled?: boolean;
}) {
  const lowBalance = a.balance_amount_eur < a.cap_amount_eur * 0.1;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      disabled={disabled}
      className={cn(
        "w-full rounded-xl border bg-card p-4 text-left transition",
        selected
          ? "border-accent ring-2 ring-accent/30"
          : "hover:border-accent/50",
        disabled ? "opacity-60" : null,
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{a.fund_name}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {formatDate(a.period_start)} – {formatDate(a.period_end)}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p
            className={cn(
              "font-display text-lg font-semibold tabular-nums",
              lowBalance ? "text-destructive" : "text-foreground",
            )}
          >
            {formatCurrencyEUR(a.balance_amount_eur)}
          </p>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
            available
          </p>
        </div>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {formatCurrencyEUR(a.used_amount_eur)} used of{" "}
        {formatCurrencyEUR(a.cap_amount_eur)}
      </p>
    </button>
  );
}
